import React, { useState, useMemo, useEffect, useRef } from 'react';

import { TableProps } from '../types';

// 本地分页，只要 TableProps 上带有 pageSize，就应用本地分页
export const usePagination = <T extends Record<string, any>>({
  scrollContainerRef,
  scrollTopOnPageChange,
  pageSize,
  pagination,
  sortedData,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  scrollTopOnPageChange: TableProps<T>['scrollTopOnPageChange'];
  pageSize: TableProps<T>['pageSize'];
  pagination: TableProps<T>['pagination'];
  sortedData?: T[];
}) => {
  const preData = useRef(sortedData);
  const [offset, setOffset] = useState(
    pageSize && pagination && (pagination.current || pagination.defaultCurrent)
      ? ((pagination.current || pagination.defaultCurrent)! - 1) * pageSize
      : 0,
  );

  const paginationConfig = useMemo(() => {
    const { onChange, ...rest } = pagination || {};

    const _onChange = (page: number, size?: number | undefined) => {
      if (scrollTopOnPageChange && scrollContainerRef.current) {
        if (scrollContainerRef.current.scrollTop) {
          scrollContainerRef.current.scrollTo({
            behavior: 'smooth',
            top: 0,
          });
        }
      }

      if (pageSize) {
        setOffset((page - 1) * pageSize);
      }

      onChange?.(page, size);
    };

    const config = {
      onChange: _onChange,
      ...rest,
    };

    if (pageSize) {
      Object.assign(config, {
        showQuickJumper: true,
        showSizeChanger: false,
        current: offset / pageSize + 1,
        defaultPageSize: pageSize,
        total: sortedData?.length,
      });
    }

    return { ...config, ...rest };
  }, [scrollTopOnPageChange, pagination, scrollContainerRef, offset, pageSize, sortedData?.length]);

  const pagedData = useMemo(() => {
    return pageSize ? sortedData?.slice(offset, offset + pageSize) : sortedData;
  }, [offset, sortedData, pageSize]);

  useEffect(() => {
    if (preData.current) {
      // data 变化时，重置 offset
      // 忽略一开始由空到有的变化
      setOffset(0);

      // 默认转到第一页，当 data 变化时
      if (pagination && pageSize) {
        pagination?.onChange?.(1, pageSize);
      }
    }

    preData.current = sortedData;
    // eslint-disable-next-line
  }, [sortedData]);

  return {
    paginationConfig,
    pagedData,
  };
};
